import { ReactNode } from 'react'
import { SIZE } from 'baseui/button'
import { Table } from 'baseui/table-semantic'

import SDButton from '@/shared/components/SDComponentsNewDesign/SDButton/SDButton'
import SDCheckbox from '@/shared/components/SDComponentsNewDesign/SDCheckbox/SDCheckbox'
import SDInput from '@/shared/components/SDComponentsNewDesign/SDInput/SDInput'
import SDLoader from '@/shared/components/SDComponentsNewDesign/SDLoader/SDLoader'
import SDModal from '@/shared/components/SDComponentsNewDesign/SDModal/SDModal'
import SDNotification from '@/shared/components/SDComponentsNewDesign/SDNotification/SDNotification'
import SDSelect from '@/shared/components/SDComponentsNewDesign/SDSelect/SDSelect'
import { Card, Header1, Header2 } from '@/shared/theme/styled-components'

import AddModal from './AddModal'
import useSuppliersManagement from './useSuppliersManagement'
import { FilterField, FiltersRow, PaginationRow } from './styled-components'
import { Supplier } from './types'

interface SDSelectParams {
  value: { id: string; label: string }[]
}

const SuppliersManagement = () => {
  const {
    suppliers,
    countrySites,
    filters,
    isLoading,
    error,
    lastPage,
    total,
    createSummary,
    t,
    isOpen,
    close,
    setIsOpen,
    searchInput,
    setSearchInput,
    updateFilters,
    toggleSupplier,
    businessVerticalOf,
  } = useSuppliersManagement()

  const siteOptions = countrySites.reduce<Record<string, string>>((options, site) => {
    options[String(site.id)] = t(`suppliersManagement:business_verticals.${site.business_type}`, site.business_type)
    return options
  }, {})

  const typeOptions = countrySites
    .filter(site => !filters.siteId || String(site.id) === filters.siteId)
    .flatMap(site => site.types)
    .reduce<Record<string, string>>((options, type) => {
      options[type] = t(`suppliersManagement:types.${type}`, type)
      return options
    }, {})

  const columns: string[] = [
    t('suppliersManagement:table.name'),
    t('suppliersManagement:table.type'),
    t('suppliersManagement:table.business_vertical'),
    t('suppliersManagement:table.status'),
    '',
  ]

  const transformSuppliers = (rows: Supplier[]): ReactNode[][] =>
    rows.map(supplier => [
      supplier.name,
      t(`suppliersManagement:types.${supplier.type}`, supplier.type),
      businessVerticalOf(supplier.site_id),
      supplier.is_active ? t('suppliersManagement:status.active') : t('suppliersManagement:status.inactive'),
      // @ts-expect-error SDButton is a JS component
      <SDButton
        id={'supplier-' + supplier.id + '-toggle-btn'}
        key={`toggle-${supplier.id}`}
        size={SIZE.compact}
        block={false}
        onClick={() => {
          const message = supplier.is_active
            ? t('suppliersManagement:confirm_deactivate')
            : t('suppliersManagement:confirm_activate')
          if (window.confirm(message)) toggleSupplier(supplier)
        }}
      >
        {supplier.is_active ? t('suppliersManagement:deactivate') : t('suppliersManagement:activate')}
      </SDButton>,
    ])

  return (
    <Card>
      <Header1 id='suppliers-title'>{t('suppliersManagement:title')}</Header1>
      <Header2>
        {total} {t('suppliersManagement:suppliers_found')}
      </Header2>

      <FiltersRow>
        <FilterField>
          {/* @ts-expect-error SDSelect is a JS component */}
          <SDSelect
            label={t('suppliersManagement:filters.business_vertical')}
            optionsList={siteOptions}
            clearable
            placeholder={t('suppliersManagement:filters.all')}
            selectedValue={filters.siteId}
            onChangeHandler={(params: SDSelectParams) => updateFilters({ siteId: params.value[0]?.id ?? '' })}
          />
        </FilterField>

        <FilterField>
          {/* @ts-expect-error SDSelect is a JS component */}
          <SDSelect
            label={t('suppliersManagement:filters.type')}
            optionsList={typeOptions}
            clearable
            placeholder={t('suppliersManagement:filters.all')}
            selectedValue={filters.type}
            onChangeHandler={(params: SDSelectParams) => updateFilters({ type: params.value[0]?.id ?? '' })}
          />
        </FilterField>

        <FilterField>
          {/* @ts-expect-error SDInput is a JS component */}
          <SDInput
            id='supplier-search'
            label={t('suppliersManagement:filters.search')}
            currentValue={searchInput}
            outerOnchange={(value: string | null) => setSearchInput(value ?? '')}
          />
        </FilterField>

        {/* @ts-expect-error SDCheckbox is a JS component */}
        <SDCheckbox
          label={t('suppliersManagement:filters.include_inactive')}
          checked={filters.includeInactive}
          onChange={(includeInactive: boolean) => updateFilters({ includeInactive })}
        />

        {/* @ts-expect-error SDButton is a JS component */}
        <SDButton id='supplier-create-btn' size={SIZE.compact} block={false} onClick={() => setIsOpen(true)}>
          {t('suppliersManagement:create_button')}
        </SDButton>
      </FiltersRow>

      <SDNotification type='negative' hidden={!error} primaryText={String(error)} />
      <SDNotification type='positive' hidden={!createSummary} primaryText={createSummary} />
      <SDLoader hidden={!isLoading} text={t('common:fetching_data')} />

      <Table columns={columns} data={transformSuppliers(suppliers)} />

      <PaginationRow>
        {/* @ts-expect-error SDButton is a JS component */}
        <SDButton
          size={SIZE.compact}
          block={false}
          disabled={filters.page <= 1}
          onClick={() => updateFilters({ page: filters.page - 1 })}
        >
          {t('common:previous')}
        </SDButton>
        <span>{`${filters.page} / ${lastPage}`}</span>
        {/* @ts-expect-error SDButton is a JS component */}
        <SDButton
          size={SIZE.compact}
          block={false}
          disabled={filters.page >= lastPage}
          onClick={() => updateFilters({ page: filters.page + 1 })}
        >
          {t('common:next')}
        </SDButton>
      </PaginationRow>

      <SDModal
        isOpen={isOpen}
        openHandler={setIsOpen}
        title={t('suppliersManagement:modal.header_create')}
        sections={<AddModal isOpen={isOpen} close={close} />}
      />
    </Card>
  )
}

export default SuppliersManagement
